import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Breadcrumb, BreadcrumbListItem, BreadcrumbListSchema } from '@shared/components/breadcrumb/models';
import { CurrentLanguageService } from '@shared/services/language';
import { SeoService } from '@shared/services/seo';
import { envConfiguredHostname } from '@shared/utils';
import { PageStore } from '@stores';

@Component({
  selector: 'app-breadcrumb',
  imports: [RouterLink],
  templateUrl: './breadcrumb.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full overflow-hidden' },
})
export class BreadcrumbComponent {
  protected readonly isOverlay = inject(PageStore).selectPath((s) => s.config.breadcrumb.isOverlay);
  protected readonly breadcrumb = inject(PageStore).selectPath((s) => s.config.breadcrumb.items);
  // The CMS opens every trail with the home of the page's language, which the house icon stands for.
  protected readonly home = computed(() => this.breadcrumb()?.[0]);
  protected readonly trail = computed(() => this.breadcrumb()?.slice(1) ?? []);

  private readonly seoService = inject(SeoService);
  private readonly language = inject(CurrentLanguageService).language;

  constructor() {
    effect(() => {
      const items = this.breadcrumb() ?? [];
      if (items.length > 0) {
        this.seoService.addJsonLdScript('breadcrumb', this.generateSchemaOrgBreadcrumb(items));
      } else {
        this.seoService.removeJsonLdScript('breadcrumb');
      }
    });
  }

  /**
   * Generates a Schema.org-compatible breadcrumb list structure from the given breadcrumb data.
   *
   * @param breadcrumbs - An array of breadcrumb objects.
   * @return A `BreadcrumbListSchema` object conforming to Schema.org standards.
   */
  private generateSchemaOrgBreadcrumb(breadcrumbs: Breadcrumb[]): BreadcrumbListSchema {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => {
        const listItem: BreadcrumbListItem = {
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.title,
        };
        if (crumb.url?.trim()) {
          listItem.item = `${envConfiguredHostname(this.language.region)}${crumb.url}`;
        }
        return listItem;
      }),
    };
  }
}
